import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FiltersRange, TextFilterItem } from 'components/Filter/Filter.types';
import FilterQuantity from 'components/Filter/FilterQuantity';
import FilterTags from 'components/Filter/FilterTags';
import { SortData } from 'components/Sort/Sort.types';
import TreeSelectSortInput from 'components/Sort/TreeSelectSortInput';
import { TIRE } from 'domains/catalog/Catalog.types';
import { getTireSortTreeDataNodes } from 'domains/tires/Tire.mapper';
import { useFetchTireBrandCategories } from 'domains/tires/Tire.requests';
import {
  getTireCatalogFilters,
  resetTireSort,
  setTireRangeFilter,
  setTireSort,
  setTireTextFilter,
} from 'domains/tires/Tire.store';
import { TireSortReference } from 'domains/tires/Tire.types';
import { getSparePartsView } from 'domains/user';
import { TireReferencesContainer } from 'pages/TiresPage/TireReferencesSection/TireReferencesContainer/TireReferencesContainer';
import { Flex, MarginBox } from 'UI';
import ViewTabs from 'UI/Tabs/ViewTabs';

interface TireReferencesSectionProps {
  searchParamsBase64: string;
  initialTextFilters?: TextFilterItem[];
  resetInitialTextFilters?: () => void;
  initialSort?: string[];
}

const TireReferencesSection = ({
  searchParamsBase64,
  initialTextFilters,
  resetInitialTextFilters,
  initialSort = [],
}: TireReferencesSectionProps) => {
  const dispatch = useDispatch();
  const [numOfProducts, setNumOfProducts] = useState<number>(0);
  const filters = useSelector(getTireCatalogFilters);
  const sparePartsView = useSelector(getSparePartsView);
  const { t } = useTranslation();
  const treeData = getTireSortTreeDataNodes(t);
  const [sortData, setSortData] = useState<SortData<TireSortReference>[]>([]);

  useFetchTireBrandCategories();

  const handleSetTextFilters = useCallback(
    (id: string, item: string) => {
      dispatch(setTireTextFilter({ id, item }));
    },
    [dispatch],
  );

  const handleSetRangeFilters = useCallback(
    (id: string, range: FiltersRange | undefined) => {
      dispatch(setTireRangeFilter({ id, range }));
    },
    [dispatch],
  );

  const handleSetSort = useCallback(
    (values: SortData<TireSortReference>[]) => {
      const keyArr = values.map((data) => data.key);
      dispatch(setTireSort(keyArr));
      setSortData(values);
    },
    [dispatch, setSortData],
  );

  const handleResetSort = useCallback(() => {
    dispatch(resetTireSort());
    setSortData([]);
  }, [dispatch, setSortData]);

  return (
    <Flex direction={'column'}>
      <Flex direction={'row'} justify={'space-between'}>
        <MarginBox my={5}>
          <MarginBox mb={-20}>
            <FilterTags
              filters={filters}
              setTextFilters={handleSetTextFilters}
              setRangeFilters={handleSetRangeFilters}
              filterSectionType={TIRE}
            />
          </MarginBox>
        </MarginBox>
        <TreeSelectSortInput<TireSortReference>
          treeData={treeData}
          initSort={initialSort}
          handleSetSort={handleSetSort}
          handleResetSort={handleResetSort}
        />
      </Flex>
      <MarginBox my={10} />
      <MarginBox mb={-30}>
        <FilterQuantity numberOfProducts={numOfProducts} />
      </MarginBox>
      <ViewTabs />
      <TireReferencesContainer
        searchParamsBase64={searchParamsBase64}
        sparePartsView={sparePartsView}
        setNumOfProducts={setNumOfProducts}
        initialTextFilters={initialTextFilters}
        resetInitialTextFilters={resetInitialTextFilters}
        sort={sortData}
      />
    </Flex>
  );
};

export default TireReferencesSection;
